import { motion } from "framer-motion";
import { Phone, Clock3 } from "lucide-react";
import { SCHEDULE, useShopStatus } from "@/lib/shopStatus";

export default function Hours() {
  const s = useShopStatus();
  const statusColor = s.isOpen ? (s.closingSoon ? "text-amber-400" : "text-emerald-400") : "text-[#FF2A3B]";
  const ringColor = s.isOpen ? (s.closingSoon ? "border-amber-400" : "border-emerald-400") : "border-[#FF2A3B]";
  const statusLabel = s.isOpen ? (s.closingSoon ? "Se închide în curând" : "Deschis acum") : "Închis acum";

  const ordered = [...SCHEDULE.slice(1), SCHEDULE[0]];

  return (
    <section id="program" data-testid="hours-section" className="relative py-20 lg:py-28 border-t border-white/5 overflow-hidden">
      <div className="absolute -bottom-24 right-0 h-[360px] w-[360px] rounded-full bg-[#FF2A3B]/8 blur-[130px]" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#0055FF]">Widget program live</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-tight">
            Program <span className="text-stroke">de lucru</span>
          </h2>

          <div data-testid="hours-live-status" className="mt-10 flex items-center gap-6">
            <div className={`relative grid place-items-center h-24 w-24 rounded-full border-2 ${ringColor}`}>
              <span className={`relative inline-flex h-4 w-4 rounded-full bg-current status-ping ${statusColor}`} />
              <span className="absolute -bottom-7 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                {s.timeLabel} · TM
              </span>
            </div>
            <div>
              <p className={`font-display text-2xl font-bold uppercase tracking-tight ${statusColor}`}>{statusLabel}</p>
              <p className="mt-1 text-sm text-slate-400">
                {s.isOpen ? `Ne poți vizita până la ${s.today.close}.` : s.nextOpenLabel || "Revenim în curând."}
              </p>
            </div>
          </div>

          <a
            data-testid="hours-hotline-cta"
            href="tel:+40786804728"
            className="mt-12 inline-flex items-center gap-3 border border-[#FF2A3B]/40 bg-[#FF2A3B]/10 hover:bg-[#FF2A3B] px-6 py-4 text-sm font-semibold transition-colors duration-300 group"
          >
            <Phone className="h-4 w-4 group-hover:rotate-12 transition-transform" />
            Linie rapidă comenzi: 0786 804 728
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <div className="border border-white/10 bg-[#131927]">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
              <Clock3 className="h-4 w-4 text-[#0055FF]" />
              <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">
                Orar săptămânal · Ora României
              </span>
            </div>
            {ordered.map((d) => {
              const realIdx = SCHEDULE.indexOf(d);
              const isToday = realIdx === s.dayIdx;
              return (
                <div
                  key={d.day}
                  data-testid={`hours-row-${d.day.toLowerCase().replace("ă", "a").replace("â", "a")}`}
                  className={`flex items-center justify-between px-6 py-4 border-b border-white/5 last:border-b-0 transition-colors ${
                    isToday ? "bg-[#0055FF]/10" : "hover:bg-white/[0.02]"
                  }`}
                >
                  <span className={`text-sm font-medium flex items-center gap-3 ${isToday ? "text-white" : "text-slate-300"}`}>
                    {d.day}
                    {isToday && (
                      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#0055FF] border border-[#0055FF]/40 px-2 py-0.5">
                        Azi
                      </span>
                    )}
                  </span>
                  <span className={`text-sm font-mono ${d.open ? (isToday ? "text-white" : "text-slate-400") : "text-[#FF2A3B]"}`}>
                    {d.open ? `${d.open} – ${d.close}` : "Închis"}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
